import React, { useEffect, useRef } from 'react';

/**
 * Parse a CSS linear-gradient string produced by celestialBackground.js
 * into the shape { direction, stops } for canvas rendering.
 *
 * Falak only emits two variants:
 *   linear-gradient(to top,    #hex 0%, #hex 40%, #hex 100%)
 *   linear-gradient(to bottom, #hex 0%, rgba(...) 50%, #hex 100%)
 *
 * Returns null when the input is already a solid colour.
 */
const parseGradient = (bg) => {
  const header = bg.match(/linear-gradient\(to (top|bottom),\s*(.+)\)$/i);
  if (!header) return null;
  const direction = header[1].toLowerCase();
  const stops = [];
  const stopRegex = /(#[0-9a-f]{3,8}|rgba?\([^)]+\))\s+(\d+)%/gi;
  let m;
  while ((m = stopRegex.exec(header[2])) !== null) {
    stops.push({ color: m[1], position: parseInt(m[2], 10) / 100 });
  }
  return stops.length ? { direction, stops } : null;
};

/**
 * DynamicBackground
 *
 * Renders Falak's dynamic sky using an HTML Canvas — NOT a CSS-gradient div.
 *
 * Rationale (identical to the moon fix):
 *   Aggressive OS-level Force-Dark on Xiaomi MIUI, Samsung One UI, Huawei
 *   EMUI and Chrome's built-in auto-dark can invert any DOM element painted
 *   with light colours, even when `color-scheme: dark` is declared. Canvas
 *   pixel data is treated as opaque image content and is NEVER inverted.
 *
 *   By painting the daytime sky, sunrise glow and sunset warmth onto a
 *   canvas we preserve Falak's core promise: the app becomes a true bridge
 *   to nature's light rhythm, regardless of the user's system dark-mode
 *   setting.
 */
const DynamicBackground = ({ celestialStyle, children }) => {
  const { background, starOpacity = 0, sunOpacity = 0 } = celestialStyle;
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const parsed = parseGradient(background);
      if (!parsed) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, w, h);
        return;
      }

      const gradient =
        parsed.direction === 'top'
          ? ctx.createLinearGradient(0, h, 0, 0)  // bottom → top
          : ctx.createLinearGradient(0, 0, 0, h); // top → bottom
      parsed.stops.forEach((s) => gradient.addColorStop(s.position, s.color));
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
    };

    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [background]);

  // Defensive inline styles — the canvas alone is immune to Force Dark, but
  // we also opt out at the CSS layer for the overlay image layers below.
  const forceDarkOptOut = {
    colorScheme: 'dark',
    forcedColorAdjust: 'none',
    WebkitForcedColorAdjust: 'none',
  };

  return (
    <div className="relative min-h-screen">
      {/* Base sky — painted onto a Canvas → immune to any OS Force-Dark */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0"
        style={{
          transition: 'opacity 3s ease-in-out',
          ...forceDarkOptOut,
        }}
        data-testid="sky-background"
      />

      {/* Starry-night texture — bitmap PNG, generally safe from Force Dark */}
      <div
        className="fixed inset-0 transition-opacity duration-[3000ms]"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/61384b03-ad61-4900-9934-3fd6157230c0/images/4da62b9a4c9c07b164db37d8181d250aac18ef43a8136a5ab420703a27184a51.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: starOpacity,
          ...forceDarkOptOut,
        }}
        data-testid="stars-layer"
      />

      {/* Sun flare (day only) — bitmap PNG blended over the canvas sky */}
      {sunOpacity > 0 && (
        <div
          className="fixed inset-0 transition-opacity duration-[3000ms]"
          style={{
            backgroundImage:
              'url(https://static.prod-images.emergentagent.com/jobs/61384b03-ad61-4900-9934-3fd6157230c0/images/5398807bae5e934c34229db206938c01f1dfc7329fdf7606797318cbbadf5cea.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: sunOpacity * 0.4,
            mixBlendMode: 'screen',
            ...forceDarkOptOut,
          }}
          data-testid="sun-flare-layer"
        />
      )}

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default DynamicBackground;
